import { LOCATION_RECENCY_BANDS, type LocationIntelPointDto, type LocationRecencyBand } from '@pams/shared';
import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  GeoJSONSourceSpecification,
  SymbolLayerSpecification,
} from 'mapbox-gl';
import type { SemanticTokens, ThemeMode } from '@/theme/tokens';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] THE MAP AS DATA — one GeoJSON source and three layers, built by pure functions so a
 *  unit test can read them without WebGL. `LocationMap.tsx` hands them to Mapbox unchanged.
 *
 *  ⚠️ MAPBOX'S OWN CLUSTERING (`cluster: true`), NOT A HAND-ROLLED GRID. Clusters are recomputed in
 *  Mapbox's worker at every zoom, so a thousand dots in one police station are one numbered circle at
 *  the commissionerate's zoom and separate dots at the street's.
 *
 *  ⚠️ A FEATURE CARRIES THE OFFICER'S ID AND BAND AND NOTHING ELSE. No name, no time: the card asks the
 *  server for those on a click. The source is drawn in this browser — Mapbox's servers are sent the
 *  token and the area being viewed (for the basemap's tiles), never these points.
 *
 *  ⚠️ COLOUR IS NOT THE ONLY CHANNEL. Green and orange are the status hues `success` and `warning` —
 *  the second non-status use of a status hue, taken at the user's direction and recorded in
 *  `DESIGN_LANGUAGE.md` — and the two are nearly identical in greyscale (measured at 3.55 and 3.54:1
 *  in light, [D4.5]). So each band also has its own SIZE, larger for more recent, and a more recent dot
 *  is drawn above an older one (`circle-sort-key`). The page's summary sentence carries every count in
 *  words, because a canvas is not readable by a screen reader at all.
 *
 *  ⚠️ THE COLOURS COME FROM THE TOKENS, AT RENDER TIME. Mapbox paints a literal colour string, not a CSS
 *  variable, so the page reads the dashboard palette for the current mode and passes the values in.
 *  Nothing here holds a colour of its own.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const POINTS_SOURCE_ID = 'officer-points';
export const CLUSTER_LAYER_ID = 'officer-point-clusters';
export const CLUSTER_COUNT_LAYER_ID = 'officer-point-cluster-count';
export const POINT_LAYER_ID = 'officer-point';

/** The layers a click or a pointer can land on, in the order they are asked. */
export const INTERACTIVE_LAYER_IDS = [CLUSTER_LAYER_ID, POINT_LAYER_ID] as const;

/**
 * [LOCATION-INTEL.1] THE BASEMAPS A VIEWER MAY CHOOSE — Mapbox's own styles, each a URL Mapbox serves. The
 * officers' dots are this page's own layers, re-added on every style load, so they sit on any of them.
 */
export const BASEMAPS = ['streets', 'satellite', 'outdoors', 'light', 'dark'] as const;
export type Basemap = (typeof BASEMAPS)[number];

export const BASEMAP_STYLE: Record<Basemap, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  /* Imagery WITH street and place names — bare imagery leaves a viewer unable to say where a dot is. */
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
};

/** Until a viewer chooses, the basemap follows the dashboard's theme: streets by day, the dark style at night. */
export const defaultBasemapFor = (mode: ThemeMode): Basemap => (mode === 'dark' ? 'dark' : 'streets');

/**
 * [LOCATION-INTEL.1] WHERE THE MAP OPENS — CENTRAL KOLKATA, BY THE USER'S INSTRUCTION, never the box around
 * the points. Lalbazar and BBD Bagh, the police headquarters. Zoom 12 is a city view, not a street one —
 * MEASURED at about 19 × 10 km at 1366 × 768 — so the southern and eastern divisions (Behala, Jadavpur,
 * Bhangar) sit outside it until a zoom-out or "All officers". A fit to every dot is one click away, because a
 * single phone that last reported from far outside the city would otherwise open the map on half of West Bengal.
 */
export const CENTRAL_KOLKATA: [number, number] = [88.351, 22.57];
export const CENTRAL_KOLKATA_ZOOM = 12;

export type MapColours = Record<LocationRecencyBand, string> & {
  cluster: string;
  clusterText: string;
  stroke: string;
};

/** The palette's values for the map — a function of the tokens, so the legend and the map read one source. */
export const mapColoursFrom = (tokens: SemanticTokens): MapColours => ({
  LAST_HOUR: tokens.success,
  LAST_SIX_HOURS: tokens.warning,
  OLDER: tokens.textTertiary,
  cluster: tokens.primary,
  clusterText: tokens.primaryForeground,
  stroke: tokens.card,
});

/** Larger for more recent — the channel that survives greyscale and colour blindness. */
export const BAND_RADIUS: Record<LocationRecencyBand, number> = { LAST_HOUR: 7, LAST_SIX_HOURS: 6, OLDER: 5 };

/**
 * A dot's outline. Mapbox draws `circle-stroke-width` OUTSIDE `circle-radius`, so a dot measures
 * `(radius + stroke) × 2` across — which is how the Map key's swatch is sized.
 */
export const DOT_STROKE_WIDTH = 2;

/** Higher draws above — so a fresh dot is never hidden under a stale one at the same spot. */
const BAND_SORT_KEY: Record<LocationRecencyBand, number> = { LAST_HOUR: 3, LAST_SIX_HOURS: 2, OLDER: 1 };

/** `['match', ['get', 'band'], <band>, <value>, …, <fallback>]`, built from the band tuple so none is missed. */
const byBand = <T extends string | number>(values: Record<LocationRecencyBand, T>, fallback: T) =>
  [
    'match',
    ['get', 'band'],
    ...LOCATION_RECENCY_BANDS.flatMap((band) => [band, values[band]]),
    fallback,
  ] as unknown as ExpressionSpecification;

/**
 * ⚠️ TYPED HERE, NOT FROM `geojson`. `mapbox-gl`'s declarations name a global `GeoJSON` namespace whose
 * package (`@types/geojson`) it does not depend on, so it is not installed; a second dependency for one
 * shape is not worth it. These two types are exactly the GeoJSON this map draws.
 */
export type PointFeature = {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: { id: string; band: LocationRecencyBand };
};
export type PointCollection = { type: 'FeatureCollection'; features: PointFeature[] };

export const pointsData = (points: readonly LocationIntelPointDto[]): PointCollection => ({
  type: 'FeatureCollection',
  features: points.map((point) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
    properties: { id: point.id, band: point.band },
  })),
});

export const pointsSource = (points: readonly LocationIntelPointDto[]): GeoJSONSourceSpecification => ({
  type: 'geojson',
  data: pointsData(points),
  cluster: true,
  clusterRadius: 50,
  /* Past street level every dot stands alone, however close. */
  clusterMaxZoom: 15,
});

export const clusterLayer = (colours: MapColours): CircleLayerSpecification => ({
  id: CLUSTER_LAYER_ID,
  type: 'circle',
  source: POINTS_SOURCE_ID,
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': colours.cluster,
    'circle-radius': ['step', ['get', 'point_count'], 15, 10, 19, 100, 24],
    'circle-stroke-width': 2,
    'circle-stroke-color': colours.stroke,
  },
});

export const clusterCountLayer = (colours: MapColours): SymbolLayerSpecification => ({
  id: CLUSTER_COUNT_LAYER_ID,
  type: 'symbol',
  source: POINTS_SOURCE_ID,
  filter: ['has', 'point_count'],
  layout: {
    'text-field': ['get', 'point_count_abbreviated'],
    /* Glyphs Mapbox hosts for every one of its own styles. */
    'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 12,
    'text-allow-overlap': true,
  },
  paint: { 'text-color': colours.clusterText },
});

export const pointLayer = (colours: MapColours): CircleLayerSpecification => ({
  id: POINT_LAYER_ID,
  type: 'circle',
  source: POINTS_SOURCE_ID,
  filter: ['!', ['has', 'point_count']],
  layout: { 'circle-sort-key': byBand(BAND_SORT_KEY, 0) },
  paint: {
    'circle-color': byBand<string>(colours, colours.OLDER),
    'circle-radius': byBand(BAND_RADIUS, BAND_RADIUS.OLDER),
    'circle-stroke-width': DOT_STROKE_WIDTH,
    'circle-stroke-color': colours.stroke,
  },
});

/** The three layers, bottom to top. */
export const pointLayers = (
  colours: MapColours,
): [CircleLayerSpecification, SymbolLayerSpecification, CircleLayerSpecification] => [
  clusterLayer(colours),
  clusterCountLayer(colours),
  pointLayer(colours),
];

/** `[[west, south], [east, north]]` around every point, or `null` when there are none. */
export const boundsOf = (points: readonly LocationIntelPointDto[]): [[number, number], [number, number]] | null => {
  const first = points[0];
  if (first === undefined) return null;
  let west = first.lng;
  let east = first.lng;
  let south = first.lat;
  let north = first.lat;
  for (const point of points) {
    west = Math.min(west, point.lng);
    east = Math.max(east, point.lng);
    south = Math.min(south, point.lat);
    north = Math.max(north, point.lat);
  }
  return [
    [west, south],
    [east, north],
  ];
};

/* [LOCATION-INTEL.1] The console's account of a Mapbox error — see the `error` listener in `LocationMap.tsx`. */

/** Any address in the text with its token masked — Mapbox appends `access_token` to every address it asks. */
const withoutToken = (text: string): string => text.replace(/access_token=[^&#\s]*/g, 'access_token=…');

/**
 * One console line for a Mapbox error: its status, its message and the address it failed on, token masked.
 * ⚠️ THE MESSAGE IS MASKED TOO: when a request fails below HTTP (a blocked or unreachable host), 3.32.0 throws
 * `Failed to fetch <the address>` with the token-bearing address in the MESSAGE and no `url` field at all.
 */
export const mapErrorLine = (error: unknown): string => {
  const { status, message, url } = (error ?? {}) as { status?: unknown; message?: unknown; url?: unknown };
  const parts = [
    typeof status === 'number' ? String(status) : null,
    typeof message === 'string' && message !== '' ? withoutToken(message) : null,
    typeof url === 'string' ? withoutToken(url) : null,
  ].filter((part): part is string => part !== null);
  return `Locational Intel map: ${parts.length === 0 ? 'an unknown error' : parts.join(' · ')}`;
};

/**
 * Whether a Mapbox error is the token refused: a 401 or 403 on any request — or 3.32.0's own revocation when
 * `/map-sessions` refuses the token, which `_revokeAuth` fires with this sentence and NO status, after which the
 * painter draws nothing at all.
 */
export const isTokenRefusal = (error: unknown): boolean => {
  const { status, message } = (error ?? {}) as { status?: unknown; message?: unknown };
  return (
    status === 401 ||
    status === 403 ||
    (typeof message === 'string' && message.startsWith('A valid Mapbox access token is required'))
  );
};

/**
 * Whether a Mapbox error belongs to one tile or source rather than to the style itself. 3.32.0 bubbles a tile's
 * error up to the map with `tile` (and `sourceId`) attached; the style request's own failure carries neither.
 */
export const isSourceError = (event: unknown): boolean => {
  const { tile, sourceId } = (event ?? {}) as { tile?: unknown; sourceId?: unknown };
  return tile !== undefined || sourceId !== undefined;
};
